/**
 * @fileoverview Dropzone de upload de midia para header de template Meta.
 *
 * Fluxo:
 *  1. Usuario seleciona arquivo (IMAGE / VIDEO / DOCUMENT)
 *  2. Valida mime + tamanho localmente
 *  3. Envia via multipart para POST /meta/templates/:id/media
 *  4. Backend faz Resumable Upload na Meta e devolve um `handle`
 *  5. Guarda handle + fileName + mimeType no state.header do builder
 *
 * Limites oficiais da Meta (espelhados no backend):
 *   IMAGE:    JPEG/PNG, ate 5 MB
 *   VIDEO:    MP4/3GP,  ate 16 MB
 *   DOCUMENT: PDF,      ate 100 MB
 */

import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  Upload,
  CheckCircle2,
  X,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  Loader,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const isDark = (theme) => theme.palette.type === "dark";

const LIMITS = {
  IMAGE: {
    maxBytes: 5 * 1024 * 1024,
    accept: "image/jpeg,image/png",
    recommended: "JPEG ou PNG · 1125×590 ou quadrada · até 5 MB",
    Icon: ImageIcon,
    label: "imagem",
  },
  VIDEO: {
    maxBytes: 16 * 1024 * 1024,
    accept: "video/mp4,video/3gpp",
    recommended: "MP4 (H.264 + AAC) · até 16 MB",
    Icon: VideoIcon,
    label: "vídeo",
  },
  DOCUMENT: {
    maxBytes: 100 * 1024 * 1024,
    accept: "application/pdf",
    recommended: "PDF · até 100 MB",
    Icon: FileText,
    label: "documento",
  },
};

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const useStyles = makeStyles((theme) => ({
  zone: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2.25),
    borderRadius: 12,
    border: isDark(theme)
      ? "1.5px dashed rgba(255,255,255,0.15)"
      : "1.5px dashed rgba(15,23,42,0.15)",
    background: isDark(theme)
      ? "rgba(255,255,255,0.02)"
      : "rgba(15,23,42,0.015)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
    transition: "border-color 0.15s ease, background 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
      background: isDark(theme)
        ? "rgba(99,102,241,0.05)"
        : "rgba(99,102,241,0.04)",
    },
  },
  zoneDragging: {
    borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
    background: isDark(theme)
      ? "rgba(99,102,241,0.1)"
      : "rgba(99,102,241,0.08)",
  },
  zoneIcon: {
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
  },
  zoneTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    marginTop: 4,
  },
  zoneHint: {
    fontSize: "0.78rem",
    color: isDark(theme) ? "rgba(255,255,255,0.5)" : "#64748b",
    textAlign: "center",
    lineHeight: 1.45,
  },
  card: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    background: isDark(theme) ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)",
    border: isDark(theme)
      ? "1px solid rgba(16,185,129,0.22)"
      : "1px solid rgba(16,185,129,0.2)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  thumb: {
    width: 56,
    height: 56,
    borderRadius: 8,
    overflow: "hidden",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isDark(theme)
      ? "rgba(255,255,255,0.06)"
      : "rgba(15,23,42,0.06)",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: "0.88rem",
    fontWeight: 600,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: {
    fontSize: "0.76rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 2,
  },
  removeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    padding: 6,
    borderRadius: 8,
    display: "inline-flex",
    "&:hover": {
      background: isDark(theme) ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: isDark(theme) ? "#fca5a5" : "#dc2626",
    },
  },
  hidden: {
    display: "none",
  },
  errorBox: {
    marginTop: theme.spacing(1),
    fontSize: "0.78rem",
    color: isDark(theme) ? "#fca5a5" : "#dc2626",
  },
}));

const MediaHeaderUploader = ({ format, header, onChange, connectionId }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);

  // Revoga blob URL ao trocar pra nova, trocar formato ou desmontar.
  // Sem isso a URL objeto fica em memoria ate o browser decidir GC.
  useEffect(() => {
    return () => {
      if (thumbUrl) URL.revokeObjectURL(thumbUrl);
    };
  }, [thumbUrl, format]);

  const cfg = LIMITS[format];
  if (!cfg) return null;

  const { Icon } = cfg;
  const hasFile = Boolean(header?.handle);

  const validate = (file) => {
    if (!cfg.accept.split(",").includes(file.type)) {
      return `Tipo não aceito. Envie ${cfg.accept.replace(/,/g, " ou ")}.`;
    }
    if (file.size > cfg.maxBytes) {
      const maxMB = Math.round(cfg.maxBytes / (1024 * 1024));
      return `Arquivo excede ${maxMB} MB (máx permitido pela Meta).`;
    }
    return null;
  };

  const handleFile = async (file) => {
    if (!connectionId) {
      toast.error("Conexão não identificada.");
      return;
    }
    const err = validate(file);
    if (err) {
      setError(err);
      return;
    }
    setError(null);

    // URL local pro preview (imagem e video). Revogada ao remover / trocar tipo.
    let localUrl = null;
    if (
      (format === "IMAGE" && file.type.startsWith("image/")) ||
      (format === "VIDEO" && file.type.startsWith("video/"))
    ) {
      localUrl = URL.createObjectURL(file);
      setThumbUrl(localUrl);
    } else {
      setThumbUrl(null);
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("format", format);
      const { data } = await api.post(
        `/meta/templates/${connectionId}/media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onChange({
        handle: data.handle,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        previewUrl: localUrl,
      });
    } catch (e) {
      toastError(e);
      if (localUrl) URL.revokeObjectURL(localUrl);
      setThumbUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset para permitir re-selecionar o mesmo arquivo
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    if (thumbUrl) {
      URL.revokeObjectURL(thumbUrl);
      setThumbUrl(null);
    }
    onChange({
      handle: "",
      fileName: "",
      mimeType: "",
      size: 0,
      previewUrl: null,
    });
    setError(null);
  };

  if (hasFile && !uploading) {
    return (
      <>
        <div className={classes.card}>
          <div className={classes.thumb}>
            {thumbUrl ? (
              <img src={thumbUrl} alt="" className={classes.thumbImg} />
            ) : (
              <Icon size={22} strokeWidth={1.5} className={classes.zoneIcon} />
            )}
          </div>
          <div className={classes.info}>
            <div className={classes.fileName} title={header.fileName}>
              {header.fileName || "arquivo"}
            </div>
            <div className={classes.meta}>
              <CheckCircle2 size={12} strokeWidth={2} color="#10b981" />
              Enviado {header.size ? `· ${formatBytes(header.size)}` : ""}
            </div>
          </div>
          <button
            type="button"
            className={classes.removeBtn}
            onClick={handleRemove}
            title="Remover"
          >
            <X size={15} strokeWidth={1.5} />
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={cfg.accept}
        className={classes.hidden}
        onChange={handleSelect}
      />
      <div
        className={`${classes.zone} ${dragging ? classes.zoneDragging : ""}`}
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {uploading ? (
          <>
            <Loader
              size={22}
              strokeWidth={1.5}
              className={classes.zoneIcon}
              style={{ animation: "spin 1s linear infinite" }}
            />
            <div className={classes.zoneTitle}>Enviando...</div>
            <div className={classes.zoneHint}>
              Aguarde — arquivo sendo carregado na Meta.
            </div>
          </>
        ) : (
          <>
            <Upload size={22} strokeWidth={1.5} className={classes.zoneIcon} />
            <div className={classes.zoneTitle}>
              Arraste ou clique para escolher {cfg.label}
            </div>
            <div className={classes.zoneHint}>{cfg.recommended}</div>
          </>
        )}
      </div>
      {error && <div className={classes.errorBox}>{error}</div>}
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </>
  );
};

export default MediaHeaderUploader;
